const DB_NAME = 'TaskManagerOfflineDB';
const DB_VERSION = 6; // Bumped for device update queue
const DEVICE_STORE = 'devices';
const FAULT_QUEUE_STORE = 'faultQueue';
const LOCATION_QUEUE_STORE = 'locationQueue';
const DEVICE_UPDATE_QUEUE_STORE = 'deviceUpdateQueue';
const DEVICE_LIST_STORE = 'deviceList';
const USER_TRAININGS_STORE = 'userTrainings';
const USER_CERTIFICATIONS_STORE = 'userCertifications';
const DEVICE_TYPES_STORE = 'deviceTypes';

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Devices store (for caching public device data)
      if (!db.objectStoreNames.contains(DEVICE_STORE)) {
        const deviceStore = db.createObjectStore(DEVICE_STORE, { keyPath: 'id' });
        deviceStore.createIndex('qr_code', 'qr_code', { unique: true });
        deviceStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Device list store (for caching the full device list)
      if (!db.objectStoreNames.contains(DEVICE_LIST_STORE)) {
        const deviceListStore = db.createObjectStore(DEVICE_LIST_STORE, { keyPath: 'key' });
        deviceListStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // User trainings store (for caching user's training completions)
      if (!db.objectStoreNames.contains(USER_TRAININGS_STORE)) {
        const userTrainingsStore = db.createObjectStore(USER_TRAININGS_STORE, { keyPath: 'key' });
        userTrainingsStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // User certifications store (for caching user's certifications/cards)
      if (!db.objectStoreNames.contains(USER_CERTIFICATIONS_STORE)) {
        const userCertificationsStore = db.createObjectStore(USER_CERTIFICATIONS_STORE, { keyPath: 'key' });
        userCertificationsStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Fault queue store (for offline fault reports)
      if (!db.objectStoreNames.contains(FAULT_QUEUE_STORE)) {
        const faultStore = db.createObjectStore(FAULT_QUEUE_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        faultStore.createIndex('created_at', 'created_at', { unique: false });
        faultStore.createIndex('synced', 'synced', { unique: false });
      }

      // Location queue store (for offline location updates)
      if (!db.objectStoreNames.contains(LOCATION_QUEUE_STORE)) {
        const locationStore = db.createObjectStore(LOCATION_QUEUE_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        locationStore.createIndex('created_at', 'created_at', { unique: false });
        locationStore.createIndex('synced', 'synced', { unique: false });
      }

      // Device update queue store (for offline device updates)
      if (!db.objectStoreNames.contains(DEVICE_UPDATE_QUEUE_STORE)) {
        const deviceUpdateStore = db.createObjectStore(DEVICE_UPDATE_QUEUE_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        deviceUpdateStore.createIndex('created_at', 'created_at', { unique: false });
        deviceUpdateStore.createIndex('synced', 'synced', { unique: false });
        deviceUpdateStore.createIndex('device_id', 'device_id', { unique: false });
      }

      // Device types store (for caching device type definitions)
      if (!db.objectStoreNames.contains(DEVICE_TYPES_STORE)) {
        const deviceTypesStore = db.createObjectStore(DEVICE_TYPES_STORE, { keyPath: 'key' });
        deviceTypesStore.createIndex('cached_at', 'cached_at', { unique: false });
      }
    };
  });
};

// Cache device data (single device with full details including trainings)
export const cacheDevice = async (deviceData) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_STORE);

  const cachedDevice = {
    ...deviceData,
    cached_at: Date.now()
  };

  await store.put(cachedDevice);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Get cached device by ID
export const getCachedDeviceById = async (deviceId) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_STORE, 'readonly');
  const store = tx.objectStore(DEVICE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get(deviceId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get cached device by QR code
export const getCachedDevice = async (qrCode) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_STORE, 'readonly');
  const store = tx.objectStore(DEVICE_STORE);
  const index = store.index('qr_code');

  return new Promise((resolve, reject) => {
    const request = index.get(qrCode);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Add fault report to offline queue
export const queueFaultReport = async (faultData) => {
  const db = await initDB();
  const tx = db.transaction(FAULT_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(FAULT_QUEUE_STORE);

  const queueItem = {
    ...faultData,
    created_at: Date.now(),
    synced: false
  };

  return new Promise((resolve, reject) => {
    const request = store.add(queueItem);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get pending fault reports
export const getPendingFaultReports = async () => {
  const db = await initDB();
  const tx = db.transaction(FAULT_QUEUE_STORE, 'readonly');
  const store = tx.objectStore(FAULT_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const all = request.result;
      const pending = all.filter(item => !item.synced);
      resolve(pending);
    };
    request.onerror = () => reject(request.error);
  });
};

// Mark fault report as synced
export const markFaultReportSynced = async (id) => {
  const db = await initDB();
  const tx = db.transaction(FAULT_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(FAULT_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (item) {
        item.synced = true;
        item.synced_at = Date.now();
        const putRequest = store.put(item);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// Add location update to offline queue
export const queueLocationUpdate = async (locationData) => {
  const db = await initDB();
  const tx = db.transaction(LOCATION_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(LOCATION_QUEUE_STORE);

  const queueItem = {
    ...locationData,
    created_at: Date.now(),
    synced: false
  };

  return new Promise((resolve, reject) => {
    const request = store.add(queueItem);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get pending location updates
export const getPendingLocationUpdates = async () => {
  const db = await initDB();
  const tx = db.transaction(LOCATION_QUEUE_STORE, 'readonly');
  const store = tx.objectStore(LOCATION_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const all = request.result;
      const pending = all.filter(item => !item.synced);
      resolve(pending);
    };
    request.onerror = () => reject(request.error);
  });
};

// Mark location update as synced
export const markLocationUpdateSynced = async (id) => {
  const db = await initDB();
  const tx = db.transaction(LOCATION_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(LOCATION_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (item) {
        item.synced = true;
        item.synced_at = Date.now();
        const putRequest = store.put(item);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// Add device update to offline queue
export const queueDeviceUpdate = async (deviceData) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_UPDATE_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_UPDATE_QUEUE_STORE);

  const queueItem = {
    ...deviceData,
    created_at: Date.now(),
    synced: false
  };

  return new Promise((resolve, reject) => {
    const request = store.add(queueItem);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get pending device updates
export const getPendingDeviceUpdates = async () => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_UPDATE_QUEUE_STORE, 'readonly');
  const store = tx.objectStore(DEVICE_UPDATE_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const all = request.result;
      const pending = all.filter(item => !item.synced);
      resolve(pending);
    };
    request.onerror = () => reject(request.error);
  });
};

// Mark device update as synced
export const markDeviceUpdateSynced = async (id) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_UPDATE_QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_UPDATE_QUEUE_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (item) {
        item.synced = true;
        item.synced_at = Date.now();
        const putRequest = store.put(item);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// Get total pending sync count (faults + locations + device updates)
export const getPendingSyncCount = async () => {
  const faults = await getPendingFaultReports();
  const locations = await getPendingLocationUpdates();
  const deviceUpdates = await getPendingDeviceUpdates();
  return faults.length + locations.length + deviceUpdates.length;
};

// Clear old cached devices (older than 7 days)
export const clearOldDeviceCache = async () => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_STORE);
  const index = store.index('cached_at');

  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

  return new Promise((resolve, reject) => {
    const request = index.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.cached_at < sevenDaysAgo) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Cache entire device list
export const cacheDeviceList = async (devices) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_LIST_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_LIST_STORE);

  const cachedList = {
    key: 'deviceList',
    devices: devices,
    cached_at: Date.now()
  };

  await store.put(cachedList);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Get cached device list
export const getCachedDeviceList = async () => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_LIST_STORE, 'readonly');
  const store = tx.objectStore(DEVICE_LIST_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get('deviceList');
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve(result.devices);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Cache user trainings
export const cacheUserTrainings = async (trainings) => {
  const db = await initDB();
  const tx = db.transaction(USER_TRAININGS_STORE, 'readwrite');
  const store = tx.objectStore(USER_TRAININGS_STORE);

  const cachedData = {
    key: 'userTrainings',
    trainings: trainings,
    cached_at: Date.now()
  };

  await store.put(cachedData);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Get cached user trainings
export const getCachedUserTrainings = async () => {
  const db = await initDB();
  const tx = db.transaction(USER_TRAININGS_STORE, 'readonly');
  const store = tx.objectStore(USER_TRAININGS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get('userTrainings');
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve(result.trainings);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Cache user certifications
export const cacheUserCertifications = async (certifications) => {
  const db = await initDB();
  const tx = db.transaction(USER_CERTIFICATIONS_STORE, 'readwrite');
  const store = tx.objectStore(USER_CERTIFICATIONS_STORE);

  const cachedData = {
    key: 'userCertifications',
    certifications: certifications,
    cached_at: Date.now()
  };

  await store.put(cachedData);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Get cached user certifications
export const getCachedUserCertifications = async () => {
  const db = await initDB();
  const tx = db.transaction(USER_CERTIFICATIONS_STORE, 'readonly');
  const store = tx.objectStore(USER_CERTIFICATIONS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get('userCertifications');
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve(result.certifications);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Cache device types
export const cacheDeviceTypes = async (types) => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_TYPES_STORE, 'readwrite');
  const store = tx.objectStore(DEVICE_TYPES_STORE);

  const cachedData = {
    key: 'deviceTypes',
    types: types,
    cached_at: Date.now()
  };

  await store.put(cachedData);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Get cached device types
export const getCachedDeviceTypes = async () => {
  const db = await initDB();
  const tx = db.transaction(DEVICE_TYPES_STORE, 'readonly');
  const store = tx.objectStore(DEVICE_TYPES_STORE);

  return new Promise((resolve, reject) => {
    const request = store.get('deviceTypes');
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve(result.types);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};
